import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { getRouteLabel } from '@/router/route-titles';

const APP_NAME = 'Queries';

export default function RouteAccessibility() {
  const location = useLocation();
  const previousPathnameRef = useRef(location.pathname);
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    const label = getRouteLabel(location.pathname);
    document.title = `${label} — ${APP_NAME}`;

    if (previousPathnameRef.current === location.pathname) return;
    previousPathnameRef.current = location.pathname;

    setAnnouncement('');
    const announceTimer = window.setTimeout(() => {
      setAnnouncement(`Открыта страница «${label}».`);
    }, 0);

    const root = document.getElementById('root') ?? document.body;
    const focusDestination = () => {
      const target = findRouteStart();
      if (!target) return false;
      if (!target.hasAttribute('tabindex')) target.tabIndex = -1;
      target.focus();
      return document.activeElement === target;
    };

    const observer = new MutationObserver(() => {
      if (focusDestination()) observer.disconnect();
    });
    observer.observe(root, { childList: true, subtree: true });

    const focusTimer = window.setTimeout(() => {
      if (focusDestination()) observer.disconnect();
    }, 0);
    const observerTimer = window.setTimeout(() => observer.disconnect(), 3_000);

    return () => {
      window.clearTimeout(announceTimer);
      window.clearTimeout(focusTimer);
      window.clearTimeout(observerTimer);
      observer.disconnect();
    };
  }, [location.pathname]);

  return (
    <p aria-atomic="true" aria-live="polite" className="sr-only" role="status">
      {announcement}
    </p>
  );
}

function findRouteStart(): HTMLElement | null {
  return document.querySelector<HTMLElement>(
    [
      '[data-page-heading]',
      'main h1',
      '[role="main"] h1',
      'h1',
      '[data-page-main]',
      'main',
      '[role="main"]',
    ].join(','),
  );
}
